import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import debounce from "lodash.debounce";
import { IconSearch } from "@tabler/icons-react";
import { AgGridReact } from "ag-grid-react";

type SearchInputProps = {
  placeholder?: string;
  // Optional: with a grid and no `onSearch`, typing drives the grid's quick
  // filter. Search boxes that are not attached to a grid (or that need their own
  // handler) pass `onSearch` and leave this out.
  gridRef?: React.RefObject<AgGridReact<any>>;
  disabled?: boolean;
  value?: string;
  onSearch?: (text: string) => void;
  // Layout only. Defaults to the standard 225px box; sites that sit in a
  // full-width toolbar or a narrow panel override the width here rather than
  // reaching for a different input.
  className?: string;
  // For search boxes that open inside a popup (e.g. a grid column filter),
  // where the caret is expected to be in the box already.
  autoFocus?: boolean;
  // Forwarded to the inner <input> so each search box is uniquely locatable by
  // UI automation. antd v6 appends a css-var hash to the input class
  // (`ant-input css-...`), so exact `@class='ant-input'` selectors no longer
  // match; a data-testid is the stable hook. Previously this prop was dropped
  // and every search box shared the hardcoded default id.
  "data-testid"?: string;
};

function CiqAgSearch(props: SearchInputProps) {
  const {
    gridRef,
    placeholder = "Search",
    disabled = false,
    value: valueProp,
    className = "w-[225px]",
    autoFocus = false,
    "data-testid": dataTestId = "ciq-ag-search-input",
    onSearch = (text: string) => {
      gridRef?.current?.api?.setGridOption("quickFilterText", text);
    }
  } = props;

  const [inputValue, setInputValue] = useState(valueProp ?? "");

  useEffect(() => {
    if (valueProp !== undefined) {
      setInputValue(valueProp);
    }
  }, [valueProp]);

  const debouncedSearch = useMemo(
    () =>
      debounce((text) => {
        onSearch(text);
      }, 500),
    [onSearch]
  );

  const handleChange = useCallback(
    (e: React.FormEvent) => {
      const text = (e.target as HTMLInputElement).value;
      setInputValue(text);
      debouncedSearch(text);
    },
    [debouncedSearch]
  );

  return (
    <div className={`${className} py-1`}>
      <Input
        type="text"
        placeholder={placeholder}
        suffix={<IconSearch size={14} />}
        value={inputValue}
        onChange={handleChange}
        disabled={disabled || (!props.onSearch && !gridRef?.current?.api)}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        data-testid={dataTestId}
      />
    </div>
  );
}

export default CiqAgSearch;
